import { useState, useEffect, createContext, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useImmerReducer } from 'use-immer';
import useSWR from 'swr';

import {
  PDPStateActions,
  IPDPStateDispatch,
  IPDPState,
  pdpStateReducer,
  INITIAL_PDP_STATE,
} from 'productTemplate/state/pdpStateReducer';
import { useWindowSize } from 'shared/hooks';
import { requestService } from 'shared/utils/services/requestService';

export const PDPStateContext = createContext<IPDPState>(null);
export const PDPDispatchContext = createContext<IPDPStateDispatch>(null);

interface IPDPStateProvider {
  children: ReactNode;
}

const fetchData = (url) => requestService({ url, method: 'GET' });

export const PDPStateProvider = ({ children }: IPDPStateProvider) => {
  const { query, asPath, events } = useRouter();
  const { isMobile } = useWindowSize();
  const [variantIdFromUrl, setVariantIdFromUrl] = useState<string>(null);

  useEffect(() => {
    const hash = /#id=((\w|-)+)/.exec(asPath);
    if (query && query.id) {
      setVariantIdFromUrl(query.id as string);
    } else if (hash && hash[1]) {
      setVariantIdFromUrl(hash[1]);
    }
  }, [query, asPath]);

  const [state, dispatch] = useImmerReducer(pdpStateReducer, INITIAL_PDP_STATE);

  const { data } = useSWR(
    variantIdFromUrl ? `/web-api/v1/products/${variantIdFromUrl}` : null,
    fetchData,
  );

  useEffect(() => {
    if (data) {
      dispatch({ type: PDPStateActions.addWindow, payload: data });
      dispatch({ type: PDPStateActions.setMobileView, view: 'detail', size: data.size });
    }
  }, [data]);

  const handleBeforeUnload = () => {
    dispatch({ type: PDPStateActions.resetWindows });
  };

  useEffect(() => {
    if (!isMobile) {
      events.on('routeChangeStart', handleBeforeUnload);
    }

    return () => {
      events.off('routeChangeStart', handleBeforeUnload);
    };
  }, [isMobile]);

  return (
    <PDPDispatchContext.Provider value={dispatch}>
      <PDPStateContext.Provider value={state}>{children}</PDPStateContext.Provider>
    </PDPDispatchContext.Provider>
  );
};
